import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Menu,
  X,
  LayoutDashboard,
  Users,
  ShoppingCart,
  Box,
  BarChart,
  Settings,
  ChevronDown,
  ChevronRight,
  LogOut,
  FileText,
  Receipt,
  RefreshCcw,
  Star,
  Shield,
} from "lucide-react";

interface LayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  label: string;
  icon: any;
  route?: string;
  children?: {
    label: string;
    route: string;
  }[];
}

const navigation: NavigationItem[] = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    route: "/admin/dashboard",
  },
  {
    label: "Users",
    icon: Users,
    children: [
      { label: "All Users", route: "/admin/users" },
      { label: "Roles & Permissions", route: "/admin/roles" },
    ],
  },
  {
    label: "Products",
    icon: Box,
    children: [
      { label: "All Products", route: "/admin/products" },
      { label: "Categories", route: "/admin/categories" },
      { label: "Sources", route: "/admin/sources" },
      { label: "Import Products via CSV", route: "/admin/products/import" },
      { label: "Import Categories via CSV", route: "/admin/categories/import" },
    ],
  },
  {
    label: "Invoices",
    icon: Receipt,
    children: [
      { label: "All Invoices", route: "/admin/invoices" },
      { label: "Generate Invoice", route: "/admin/invoices/new" },
    ],
  },
  {
    label: "Coupons",
    icon: FileText,
    route: "/admin/coupons",
  },
  {
    label: "Reviews",
    icon: Star,
    route: "/admin/reviews",
  },
  {
    label: "Orders",
    icon: ShoppingCart,
    children: [
      { label: "Internal Orders", route: "/admin/orders" },
      { label: "Back Market Orders", route: "/admin/orders/backmarket" },
    ],
  },
  {
    label: "Sync Center",
    icon: RefreshCcw,
    children: [
      { label: "eBay Integration", route: "/admin/ebay" },
        { label: "BackMarket Integration", route: "/admin/backmarket" },
      { label: "Sync Logs", route: "/admin/sync/logs" },
      { label: "Sync Schedules", route: "/admin/sync/schedules" },
    ],
  },
  {
    label: "Reports",
    icon: BarChart,
    children: [
      { label: "F8 Internal Sales", route: "/admin/reports/sales/f8-internal" },
      { label: "BackMarket Sales", route: "/admin/reports/sales/backmarket" },
      { label: "Combined Sales", route: "/admin/reports/sales" },
      { label: "Inventory Report", route: "/admin/reports/inventory" },
    ],
  },
  {
    label: "Content",
    icon: FileText,
    children: [
      { label: "Slide Management", route: "/admin/slides" },
      { label: "Banner Management", route: "/admin/banners" },
      { label: "Policy Management", route: "/admin/policies" },
      { label: "SEO Management", route: "/admin/seo" },
    ],
  },
  {
    label: "Settings",
    icon: Settings,
    children: [
      { label: "General Settings", route: "/admin/settings/general" },
      { label: "Shipping Methods", route: "/admin/settings/shipping-methods" },
      { label: "Security Settings", route: "/admin/settings/security" },
    ],
  },
  {
    label: "Audit Logs",
    icon: Shield,
    route: "/admin/audit-logs",
  },
];

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    // Initialize with expanded items based on current route
    const initialExpanded = new Set<string>();
    const currentPath = window.location.pathname;

    navigation.forEach((item) => {
      if (item.children?.some((child) => currentPath.startsWith(child.route))) {
        initialExpanded.add(item.label);
      }
    });

    return initialExpanded;
  });
  const { admin, logout } = useAuth();
  const location = useLocation();

  const handleLogout = () => {
    logout();
  };

  const toggleExpanded = (label: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(label)) {
        newSet.delete(label);
      } else {
        newSet.add(label);
      }
      return newSet;
    });
  };

  const isRouteActive = (route: string) => {
    return (
      location.pathname === route || location.pathname.startsWith(route + "/")
    );
  };

  const isParentActive = (item: NavigationItem) => {
    if (item.route) {
      return isRouteActive(item.route);
    }
    return item.children?.some((child) => isRouteActive(child.route)) || false;
  };

  // When sibling routes are prefixes of each other (e.g. /admin/invoices and
  // /admin/invoices/new), highlight only the most specific match so a single
  // child appears active.
  const activeChildRoute = (item: NavigationItem): string | null => {
    if (!item.children) return null;
    const matches = item.children
      .map((c) => c.route)
      .filter((r) => isRouteActive(r))
      .sort((a, b) => b.length - a.length);
    return matches[0] ?? null;
  };

  const renderNavigationItem = (
    item: NavigationItem,
    isMobile: boolean = false,
  ) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.label);
    const isActive = isParentActive(item);
    const activeRoute = activeChildRoute(item);

    if (!hasChildren && item.route) {
      // Simple navigation item without children
      return (
        <Link
          key={item.label}
          to={item.route}
          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
            isActive
              ? "bg-primary-100 text-primary-900"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          }`}
          onClick={() => isMobile && setSidebarOpen(false)}
        >
          <item.icon className="mr-3 h-5 w-5" />
          {item.label}
        </Link>
      );
    }

    // Navigation item with children
    return (
      <div key={item.label}>
        <button
          onClick={() => toggleExpanded(item.label)}
          className={`group flex w-full items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
            isActive
              ? "bg-primary-100 text-primary-900"
              : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
          }`}
        >
          <div className="flex items-center">
            <item.icon className="mr-3 h-5 w-5" />
            {item.label}
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {isExpanded && item.children && (
          <div className="ml-6 mt-1 space-y-1">
            {item.children.map((child) => (
              <Link
                key={child.label}
                to={child.route}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  child.route === activeRoute
                    ? "bg-primary-50 text-primary-700 border-l-2 border-primary-500"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                }`}
                onClick={() => isMobile && setSidebarOpen(false)}
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        ></div>
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">F8 Admin</h1>
            <button onClick={() => setSidebarOpen(false)}>
              <X className="h-6 w-6 text-gray-400" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => renderNavigationItem(item, true))}
          </nav>
          <div className="px-4 py-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 w-full"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">F8 Admin</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => renderNavigationItem(item, false))}
          </nav>
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-medium text-sm">
                  {admin?.full_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {admin?.full_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{admin?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 w-full"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"></div>
              <div className="flex items-center gap-x-4">
                <span className="text-sm text-gray-700">
                  Welcome, {admin?.full_name}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main>{children}</main>
      </div>
    </div>
  );
}
